// The trips table the year page and the big log's trips view share: one row
// per trip (tripsInYear / tripsInLogbook rows -- { key, name, entries,
// summary }), expandable in place to show that trip's entries.
//
// Row click navigates (onSelectRow -- the trip page for a named row, the entry
// for an unfiled one); the chevron is the separate control that expands, and
// its CELL stops the click, not just the button -- a disabled or mis-hit
// button lets the click carry on up to the <tr>, which would navigate instead
// (TripGroup's Log-column rule).
//
// `unfiledLabel` is the page's own wording for a row with no trip name --
// tripsInLogbook deliberately yields '' and leaves the label to whoever
// renders it. Never feed an unfiled 'e:' row through entriesForTripKey (it
// refuses non-joinable keys and answers []); `row.entries` already holds the
// entry, oldest-first, which is what the expansion renders.
//
// `photosByEntry` / `onOpenPhoto` are a straight pass-through to that
// expansion's table: this component owns no photo state and makes no read of
// its own, so a host that loads photos (Logbook, LogbookYear) hands them down
// and one that doesn't hands down nothing, which is the shared table's
// already-tested default-absent path.

import React, { useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Table from 'react-bootstrap/Table';
import { listColumnsForType } from '../../fleet-shared/LogbookTypes.mjs';
import { tripFieldForType, displayHours, displayNm } from '../../fleet-shared/LogbookTrips.mjs';
import { tripGroupDateRange } from '../../fleet-shared/TripGroups.mjs';
import { entryWithResolvedEndpoints } from '../../fleet-shared/TripRoute.mjs';
import LogbookEntriesTable from './LogbookEntriesTable';

// The chevron cell + Dates | Trip | Entries | Hours | Distance.
const COLUMN_COUNT = 6;

// An expanded row's entries table, its own component so the gazetteer resolve
// is memoized per row rather than redone for every row on every render.
// Display copies only -- onSelectEntry's caller re-looks-up the stored row.
//
// The two photo props are a straight pass-through: no gating, no defaulting,
// no state of its own. A host that passes neither (LogbookYear) hands the
// shared table `undefined` for both, which is its already-tested default-absent
// path. No `tripKey` or `showDetails` either -- inside one trip the trip name
// is the row's own label, and the expansion navigates by row click.
function ExpandedEntries({
  row,
  columns,
  placeIndex,
  showNotes,
  onSelectEntry,
  photosByEntry,
  onOpenPhoto,
}) {
  const displayEntries = useMemo(
    () => row.entries.map((entry) => entryWithResolvedEndpoints(entry, placeIndex)),
    [row.entries, placeIndex]
  );
  return (
    <LogbookEntriesTable
      entries={displayEntries}
      columns={columns}
      placeIndex={placeIndex}
      showNotes={showNotes}
      onSelectEntry={onSelectEntry}
      photosByEntry={photosByEntry}
      onOpenPhoto={onOpenPhoto}
    />
  );
}

export function LogbookTripsTable({
  rows,
  typeId,
  placeIndex,
  showNotes,
  onSelectRow,
  onSelectEntry,
  unfiledLabel,
  photosByEntry,
  onOpenPhoto,
}) {
  // Which rows are expanded, by trip key. Local state: expansion is a peek,
  // not an address, so it doesn't belong in the URL the way the view does.
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());

  // Trip Name is redundant inside one trip's expansion -- it's the row's own
  // label -- so it drops, the trip page's rule.
  const entryColumns = useMemo(() => {
    const tripField = tripFieldForType(typeId);
    return listColumnsForType(typeId).filter((field) => field.key !== tripField?.key);
  }, [typeId]);

  function toggleExpanded(key) {
    setExpandedKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <Table responsive hover className="align-middle mb-0">
      <thead className="bg-body-tertiary">
        <tr>
          {/* The expansion chevron's column; the header has nothing to say. */}
          <th scope="col" aria-label="Expand" style={{ width: 44 }}></th>
          <th scope="col">Dates</th>
          <th scope="col">Trip</th>
          <th scope="col" className="text-end">Entries</th>
          <th scope="col" className="text-end">Hours</th>
          <th scope="col" className="text-end">Distance</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const expanded = expandedKeys.has(row.key);
          const label = row.name || unfiledLabel;
          return (
            <React.Fragment key={row.key}>
              <tr
                onClick={() => onSelectRow(row)}
                role="button"
                style={{ cursor: 'pointer' }}
              >
                <td
                  className="align-middle text-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    aria-expanded={expanded}
                    aria-label={`${expanded ? 'Hide' : 'Show'} entries for ${label}`}
                    onClick={(e) => { e.stopPropagation(); toggleExpanded(row.key); }}
                  >
                    <i
                      className={`fa-solid ${expanded ? 'fa-chevron-down' : 'fa-chevron-right'}`}
                      aria-hidden="true"
                    ></i>
                  </Button>
                </td>
                <td className="align-middle entered-data text-nowrap">
                  {tripGroupDateRange(row.summary)}
                </td>
                <td className="align-middle entered-data">{label}</td>
                <td className="align-middle entered-data text-end">{row.summary.count}</td>
                <td className="align-middle entered-data text-end">
                  {displayHours(row.summary.totalHours)}
                </td>
                <td className="align-middle entered-data text-end">
                  {displayNm(row.summary.totalDistanceNm)}
                </td>
              </tr>
              {expanded && (
                <tr>
                  <td colSpan={COLUMN_COUNT} className="pt-0">
                    <ExpandedEntries
                      row={row}
                      columns={entryColumns}
                      placeIndex={placeIndex}
                      showNotes={showNotes}
                      onSelectEntry={onSelectEntry}
                      photosByEntry={photosByEntry}
                      onOpenPhoto={onOpenPhoto}
                    />
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </Table>
  );
}

export default LogbookTripsTable;
